/**
 * DICOM routing rule service — create/listener routing configuration.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiDelete, apiGet, apiPost, apiPut, ApiError } from '../utils/apiClient';

export interface CreateDicomRoutingRuleRequest {
  name: string;
  modality_ids: number[];
  radiology_test_ids?: number[];
  match_ae_title?: string;
  hub_facility_id?: number;
  match_priority?: string;
  precedence?: number;
  at_title?: string;
  destination_type: string;
  destination_ae: string;
  is_active: boolean;
}

export interface UpdateDicomRoutingRuleRequest {
  is_active?: boolean;
}

export interface DicomRoutingRuleModality {
  id: number;
  name: string;
}

export interface DicomRoutingRuleFacilitySummary {
  id: number;
  facility_name: string;
  facility_type?: string;
  pacs_ae_title?: string;
}

export interface DicomRoutingRule {
  id: number;
  spoke_facility_id?: number;
  name: string;
  modality_ids: number[];
  radiology_test_ids?: number[];
  match_ae_title?: string | null;
  hub_facility_id?: number | null;
  match_priority?: string | null;
  precedence: number;
  at_title?: string | null;
  destination_type: string;
  destination_ae: string;
  is_active: boolean;
  is_deleted?: boolean;
  modalities?: DicomRoutingRuleModality[];
  HubFacility?: DicomRoutingRuleFacilitySummary;
  SpokeFacility?: DicomRoutingRuleFacilitySummary;
}

export interface DicomRoutingRulesPagination {
  currentPage: number;
  totalPages: number;
  totalRules: number;
  rulesPerPage: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface CreateDicomRoutingRuleApiResponse {
  success?: boolean;
  message?: string;
  data?: DicomRoutingRule;
}

interface UpdateDicomRoutingRuleApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
  data?: DicomRoutingRule;
}

interface ListDicomRoutingRulesApiResponse {
  success?: boolean;
  message?: string;
  data?: DicomRoutingRule[];
  pagination?: DicomRoutingRulesPagination;
}

interface DeleteDicomRoutingRuleApiResponse {
  success?: boolean;
  message?: string;
  error?: string;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.response && typeof error.response === 'object') {
      const response = error.response as { message?: string; error?: string };
      if (response.message) {
        return response.message;
      }
      if (response.error) {
        return response.error;
      }
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Routing rule request failed';
}

export async function getDicomRoutingRules(page = 1): Promise<{
  success: boolean;
  data?: DicomRoutingRule[];
  pagination?: DicomRoutingRulesPagination;
  error?: string;
  message?: string;
}> {
  try {
    const response = await apiGet<ListDicomRoutingRulesApiResponse>(
      API_ENDPOINTS.DICOM_ROUTING_RULES.GET({ page }),
      { requireAuth: true }
    );
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to fetch routing rules' };
    }

    if (Array.isArray(body?.data)) {
      return {
        success: true,
        data: body.data,
        pagination: body.pagination,
        message: body.message,
      };
    }

    return { success: false, error: 'Invalid routing rules response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function createDicomRoutingRule(
  payload: CreateDicomRoutingRuleRequest
): Promise<{ success: boolean; data?: DicomRoutingRule; error?: string; message?: string }> {
  try {
    const response = await apiPost<CreateDicomRoutingRuleApiResponse>(
      API_ENDPOINTS.DICOM_ROUTING_RULES.CREATE,
      payload,
      { requireAuth: true }
    );
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to create routing rule' };
    }

    if (body?.data) {
      return { success: true, data: body.data, message: body.message };
    }

    return { success: false, error: 'Invalid routing rule response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function deleteDicomRoutingRule(
  id: number | string
): Promise<{ success: boolean; error?: string; message?: string }> {
  try {
    const response = await apiDelete<DeleteDicomRoutingRuleApiResponse>(
      API_ENDPOINTS.DICOM_ROUTING_RULES.DELETE(id),
      { requireAuth: true }
    );
    const body = response.data;

    if (body?.success === false) {
      return {
        success: false,
        error: body.error || body.message || 'Failed to delete routing rule',
      };
    }

    return { success: true, message: body?.message };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export async function updateDicomRoutingRule(
  id: number | string,
  payload: UpdateDicomRoutingRuleRequest
): Promise<{ success: boolean; data?: DicomRoutingRule; error?: string; message?: string }> {
  try {
    const response = await apiPut<UpdateDicomRoutingRuleApiResponse>(
      API_ENDPOINTS.DICOM_ROUTING_RULES.UPDATE(id),
      payload,
      { requireAuth: true }
    );
    const body = response.data;

    if (body?.success === false) {
      return {
        success: false,
        error: body.error || body.message || 'Failed to update routing rule',
      };
    }

    if (body?.data) {
      return { success: true, data: body.data, message: body.message };
    }

    return { success: false, error: 'Invalid routing rule response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}

export interface DicomRoutingRuleReorderItem {
  id: number;
  precedence: number;
}

export async function reorderDicomRoutingRules(
  rules: DicomRoutingRuleReorderItem[]
): Promise<{ success: boolean; data?: DicomRoutingRule[]; error?: string; message?: string }> {
  try {
    const response = await apiPut<ListDicomRoutingRulesApiResponse>(
      API_ENDPOINTS.DICOM_ROUTING_RULES.REORDER,
      { rules },
      { requireAuth: true }
    );
    const body = response.data;

    if (body?.success === false) {
      return { success: false, error: body.message || 'Failed to reorder routing rules' };
    }

    if (Array.isArray(body?.data)) {
      return { success: true, data: body.data, message: body.message };
    }

    return { success: false, error: 'Invalid reorder response' };
  } catch (error) {
    return { success: false, error: extractErrorMessage(error) };
  }
}
